/**
 * Hex parsing, validation, and mixing. Pure; no DOM, no app knowledge.
 *
 * Validation exists because a CSS custom property value is an injection sink and every color here is
 * merchant-supplied. `#FFF; } body { display:none` is the attack, and the answer is an allowlist
 * regex rather than an attempt to sanitize.
 */

export interface Rgb {
  r: number;
  g: number;
  b: number;
}

/**
 * Six digits only. Three-digit shorthand is not in the contract, so accepting it would mean
 * accepting a shape the backend never promised — and every widening of this regex is a widening of
 * what can reach the CSSOM.
 */
const HEX_RE = /^#[0-9a-f]{6}$/i;

export function isValidHex(value: unknown): value is string {
  return typeof value === 'string' && HEX_RE.test(value);
}

/**
 * Canonical form is lowercase `#rrggbb`.
 *
 * Every derived token comes out of `rgbToHex`, which emits lowercase, so without normalizing the
 * merchant's input a palette would produce `--vp-on-primary: #FFFFFF` next to
 * `--vp-primary-hover: #1850c9`. Beyond being untidy, it makes token equality checks in tests and
 * in the DEV substitution logs depend on how the merchant happened to type their brand color.
 */
export function normalizeHex(value: string): string {
  return value.toLowerCase();
}

export function hexToRgb(hex: string): Rgb {
  if (!isValidHex(hex)) {
    throw new Error('hexToRgb requires a validated #RRGGBB string');
  }
  return {
    r: Number.parseInt(hex.slice(1, 3), 16),
    g: Number.parseInt(hex.slice(3, 5), 16),
    b: Number.parseInt(hex.slice(5, 7), 16),
  };
}

function channelToHex(value: number): string {
  const clamped = Math.max(0, Math.min(255, Math.round(value)));
  return clamped.toString(16).padStart(2, '0');
}

export function rgbToHex({ r, g, b }: Rgb): string {
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
}

/**
 * Linear interpolation in sRGB space. `t = 0` returns `from`, `t = 1` returns `to`.
 *
 * Deliberately sRGB rather than a perceptual space: these mixes produce surfaces and borders that
 * sit within a few percent of the background, where the two are visually indistinguishable, and
 * every result is then contrast-checked anyway. A perceptual conversion would add code and a
 * dependency to move a border by an imperceptible amount.
 */
export function mix(from: string, to: string, t: number): string {
  const a = hexToRgb(from);
  const b = hexToRgb(to);
  const ratio = Math.max(0, Math.min(1, t));
  return rgbToHex({
    r: a.r + (b.r - a.r) * ratio,
    g: a.g + (b.g - a.g) * ratio,
    b: a.b + (b.b - a.b) * ratio,
  });
}
